import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import { OrderTypes } from "@shared/features/trades/common/types";
import { useContext, useState } from "react";
import {
  LONG_SHORT_TO_DISPLAY,
  MARGIN_TYPE_TO_DISPLAY,
} from "@shared/common/constants";
import {
  BaseCreateTradeRequest,
  CreateSignalRequest,
  CreateTradeRequest,
  DisplayResultMessage,
  LongShort,
  LanguageNameSpaces,
} from "@shared/types";
import {
  ORDER_TYPE_TO_CREATE_TRADE_TAB,
  TRADE_TYPE_TO_DISPLAY_NAME,
} from "@shared/features/tradingTerminal/common/constants";
import {
  ExistingQuantityOrderData,
  TradeTypes,
} from "@shared/features/tradingTerminal/common/types";
import {
  getCreateTradeOrders,
  getNumberOrdersData,
} from "@shared/features/tradingTerminal/common/utils";
import { omit } from "lodash";
import { useSummaryAmount } from "@shared/features/tradingTerminal/common/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";
import { formatTimeoutDisplay } from "@shared/features/bots/utils";
import { translateOptionName } from "@shared/common/i18n/optionNames";

const useSummaryModal = (closeModal?: () => void) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const {
    symbols,
    account,
    quantity,
    quantityAmount,
    extraInfo,
    entryOrders,
    takeProfitOrders,
    stopOrders,
    ordersErrors,
    useTakeProfitOrders,
    useStopOrders,
    setCreateTradeTab,
    useTrailing,
    trailingEntry,
    trailingTakeProfit,
    trailingStop,
    createTrade,
    createTradeLoading,
    createSignalLoading,
    currentPrice,
    precisionConfig,
    isMobile,
    numErrors,
    convertMainCoinAmountToUSD,
    quantityCoin,
    existingQuantityOrder,
    isSignal,
    exchanges,
    amount,
    amountType,
    ignoreOrdersRatios,
    createSignal,
    subGroupIds,
    stopTimeout,
  } = useContext(TradingTerminalContext)!;

  const createTradeButtonDisabled = numErrors > 0;
  const [mobileSummaryTab, setMobileSummaryTab] = useState(OrderTypes.ENTRY);
  const { trade_type } = extraInfo || {};
  const formattedAmount = useSummaryAmount();

  const [expanded, setExpanded] = useState({
    [OrderTypes.ENTRY]: true,
    [OrderTypes.TAKE_PROFIT]: true,
    [OrderTypes.STOP]: true,
  });

  const orders = {
    [OrderTypes.ENTRY]: entryOrders,
    [OrderTypes.TAKE_PROFIT]: takeProfitOrders,
    [OrderTypes.STOP]: stopOrders,
  };

  const useOrders = {
    [OrderTypes.ENTRY]: true,
    [OrderTypes.TAKE_PROFIT]: useTakeProfitOrders,
    [OrderTypes.STOP]: useStopOrders,
  };

  const ordersToUse = {
    [OrderTypes.ENTRY]: entryOrders,
    [OrderTypes.TAKE_PROFIT]: useTakeProfitOrders ? takeProfitOrders : [],
    [OrderTypes.STOP]: useStopOrders ? stopOrders : [],
  };

  const usdQuantityAmount = convertMainCoinAmountToUSD?.(
    Number(quantityAmount),
    true
  );

  const displayExistingAmount = `${quantity} ${quantityCoin} (${
    usdQuantityAmount || 0
  } USD)`;

  const getCancelTradeOnFirstTpDisplay = () => {
    const value = extraInfo?.cancel_on_first_target_reached;
    if (isSignal && value === null) return t("labels.dynamic");
    return value ? t("labels.enabled") : t("labels.disabled");
  };

  const generalDisplayData = {
    amount: formattedAmount,
    quantity: displayExistingAmount,
    tradeType: t(
      TRADE_TYPE_TO_DISPLAY_NAME[
        extraInfo?.trade_type || TradeTypes.REGULAR
      ] as TranslationKeys<LanguageNameSpaces.TRADING_TERMINAL>
    ),
    direction: translateOptionName(
      LONG_SHORT_TO_DISPLAY[
        extraInfo?.reverse ? LongShort.SHORT : LongShort.LONG
      ]
    ),
    margin:
      extraInfo?.margin_type &&
      ` - ${translateOptionName(
        MARGIN_TYPE_TO_DISPLAY[extraInfo.margin_type]
      )} ${extraInfo?.leverage}x`,
    cancelTradeOnFirstTp: getCancelTradeOnFirstTpDisplay(),
    stopTimeout: formatTimeoutDisplay((stopTimeout || 0) * 60, tCommon),
  };

  const shouldDisplayField = (title: string) => {
    if (isSignal && title === t(EXISTING_AMOUNT_TITLE)) return false;
    return title !== t("labels.leverage") || !!extraInfo?.margin_type;
  };

  const getExistingQuantityOrderData = (): ExistingQuantityOrderData => {
    return {
      is_existing_coins: true,
      quantity: Number(quantity),
      amount: quantityAmount,
      price: currentPrice || 0,
      ratio: 1,
    };
  };

  const onSubmitCreateTrade = (
    baseRequest: BaseCreateTradeRequest,
    displayResultMessage: DisplayResultMessage,
    onTradeCreated: () => void
  ) => {
    if (!account || !trade_type) return;
    const createTradeRequest: CreateTradeRequest = {
      ...baseRequest,
      account_id: account.client_id,
      trade_type,
    };

    displayResultMessage(
      createTrade(createTradeRequest).unwrap(),
      t("messages.success.trade_created"),
      t("messages.errors.create_trade_failed")
    )
      .then(onTradeCreated)
      .catch(() => {});
  };

  const onSubmitCreateSignal = (
    baseRequest: BaseCreateTradeRequest,
    displayResultMessage: DisplayResultMessage,
    onTradeCreated: () => void
  ) => {
    if (!trade_type || !exchanges || !subGroupIds) return;
    const createSignalRequest: CreateSignalRequest = {
      ...baseRequest,
      exchanges,
      signal_type: trade_type,
      amount_type: amountType ?? null,
      amount_value: amountType ? Number(amount) / 100 : null,
      sub_group_ids: subGroupIds,
    };
    displayResultMessage(
      createSignal(createSignalRequest).unwrap(),
      t("messages.success.signal_created"),
      t("messages.errors.create_signal_failed")
    )
      .then(onTradeCreated)
      .catch(() => {});
  };

  const onSubmit = (
    displayResultMessage: DisplayResultMessage,
    onTradeCreated: () => void
  ) => {
    if (!symbols[0] || !extraInfo) return;
    const numberOrdersData = getNumberOrdersData(ordersToUse);
    const createTradeOrdersData = getCreateTradeOrders(
      numberOrdersData,
      precisionConfig,
      isSignal,
      ignoreOrdersRatios,
      stopTimeout
    );
    const buySection = Number(quantity)
      ? [
          getExistingQuantityOrderData(),
          ...createTradeOrdersData[OrderTypes.ENTRY],
        ]
      : createTradeOrdersData[OrderTypes.ENTRY];
    const baseRequest: BaseCreateTradeRequest = {
      ...omit(extraInfo, ["trade_type"]),
      symbol: symbols[0],
      buy_section: buySection,
      sell_section: createTradeOrdersData[OrderTypes.TAKE_PROFIT],
      stop_section: createTradeOrdersData[OrderTypes.STOP],
      trailing_entry: useTrailing[OrderTypes.ENTRY] ? trailingEntry : null,
      trailing_take_profit: useTrailing[OrderTypes.TAKE_PROFIT]
        ? trailingTakeProfit
        : null,
      trailing_stop: useTrailing[OrderTypes.STOP] ? trailingStop : null,
    };
    if (isSignal) {
      onSubmitCreateSignal(baseRequest, displayResultMessage, onTradeCreated);
    } else {
      onSubmitCreateTrade(baseRequest, displayResultMessage, onTradeCreated);
    }
  };

  const showZeroRatioTargetsWarning = !!Object.values(ordersToUse).find(
    (orders) => orders.find((order) => order.percent === "0")
  );

  const getShowOrders = (orderType: OrderTypes) =>
    useOrders[orderType] &&
    (!!orders[orderType].length ||
      (orderType === OrderTypes.ENTRY && !!existingQuantityOrder));

  const onAddOrderClick = (orderType: OrderTypes) => {
    setCreateTradeTab(ORDER_TYPE_TO_CREATE_TRADE_TAB[orderType]);
    closeModal?.();
  };

  return {
    symbols,
    account,
    ordersErrors,
    expanded,
    setExpanded,
    orders,
    useOrders,
    generalDisplayData,
    setCreateTradeTab,
    onSubmit,
    createTradeLoading,
    createSignalLoading,
    showZeroRatioTargetsWarning,
    isMobile,
    mobileSummaryTab,
    setMobileSummaryTab,
    createTradeButtonDisabled,
    getShowOrders,
    onAddOrderClick,
    shouldDisplayField,
  };
};

export default useSummaryModal;

export const EXISTING_AMOUNT_TITLE = "labels.existing_amount";
